// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Button, Divider, Paper, Stack, Typography } from '@mui/material';
import { FormEventHandler, PropsWithChildren } from 'react';

interface Props {
  /**
   * The form's title.
   */
  title: string;

  /**
   * If true, the save button is enabled.
   */
  isDirty: boolean;

  /**
   * If true, the save and reset buttons are disabled.
   */
  isDisabled: boolean;

  /**
   * A callback fired when the form requests to be reset.
   */
  onReset: () => void;

  /**
   * A callback fired when the form is submitted.
   */
  onSubmit: FormEventHandler<HTMLFormElement>;
}

/**
 * A card component that renders a form with a title, save button, and reset
 * button.
 */
export function FormCard(props: PropsWithChildren<Props>) {
  return (
    <Paper
      variant="outlined"
      component="form"
      onSubmit={props.onSubmit}
      sx={{ overflow: 'hidden' }}
    >
      <Stack
        useFlexGap
        spacing={1}
        paddingX={2}
        paddingY={2}
        direction="row"
        alignItems="center"
        bgcolor="grey.50"
      >
        <Typography variant="overline" marginRight="auto">
          {props.title}
        </Typography>
        {props.isDirty && (
          <Button
            size="small"
            onClick={props.onReset}
            disabled={props.isDisabled}
          >
            Reset
          </Button>
        )}
        <Button
          type="submit"
          size="small"
          variant="contained"
          disabled={props.isDisabled}
        >
          Save
        </Button>
      </Stack>
      <Divider />
      {props.children}
    </Paper>
  );
}
